import { useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { PERMISSIONS } from '@pms/shared';
import { usePermission } from '@/hooks/usePermission';
import { Tabs } from '@/components/ui/tabs';
import { DepartmentsSection } from './DepartmentsSection';
import { DesignationsSection } from './DesignationsSection';
import { LeaveConfigSection } from './LeaveConfigSection';
import { PenaltyConfigSection } from './PenaltyConfigSection';
import { SalaryMultipliersSection } from './SalaryMultipliersSection';
import { RecruitmentConfigSection } from './RecruitmentConfigSection';
import { LoansAdvancesConfigSection } from './LoansAdvancesConfigSection';
import { DeletePunchPermissionSection } from './DeletePunchPermissionSection';
import { DeleteAccessSection } from './DeleteAccessSection';
import { MessageConfigSection } from './MessageConfigSection';
import { FaceScanKeystoresSection } from './FaceScanKeystoresSection';

const TAB_KEYS = ['departments', 'designations', 'leave_config', 'penalty_config', 'salary_multipliers', 'loans_advances', 'recruitment', 'delete_punch_permission', 'delete_access_config', 'wati', 'face_scan_keystores'] as const;
type TabKey = (typeof TAB_KEYS)[number];

const TAB_LABELS: Record<TabKey, string> = {
  departments:             'Departments',
  designations:            'Designations',
  leave_config:            'Leave Config',
  penalty_config:          'Penalty Config',
  salary_multipliers:      'Salary Multipliers',
  loans_advances:          'Loans & Advances',
  recruitment:             'Recruitment',
  delete_punch_permission: 'Delete Permissions',
  delete_access_config:    'Delete Access',
  // Key stays 'wati' so existing ?tab=wati links keep working; the tab now owns both
  // transports, hence the label.
  wati:                    'Message',
  face_scan_keystores:     'Face Scan Keystores',
};

export function HrmsConfigPage() {
  const { has } = usePermission();
  const [searchParams, setSearchParams] = useSearchParams();

  const visibleTabs = TAB_KEYS.filter((k) => {
    if (k === 'recruitment') return has(PERMISSIONS.RECRUITMENT_VIEW);
    if (k === 'delete_punch_permission') return has(PERMISSIONS.ATTENDANCE_MANAGE);
    if (k === 'delete_access_config')    return has(PERMISSIONS.DELETE_ACCESS_MANAGE);
    if (k === 'wati')                    return has(PERMISSIONS.WATI_CONFIG_VIEW);
    if (k === 'face_scan_keystores')     return has(PERMISSIONS.FACE_SCAN_KEYSTORES_VIEW);
    return true;
  });

  const requested = (searchParams.get('tab') as TabKey) || 'departments';
  const initial = visibleTabs.includes(requested) ? requested : 'departments';
  const [active, setActive] = useState<TabKey>(initial);

  const onChange = (key: string) => {
    setActive(key as TabKey);
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      if (key === 'departments') next.delete('tab');
      else next.set('tab', key);
      return next;
    });
  };

  return (
    <div className="space-y-4">
      <div>
        <h1 className="text-xl sm:text-2xl font-semibold tracking-tight">HRMS Config</h1>
        <p className="text-sm text-muted-foreground">
          Foundational settings shared across all HRMS sub-modules. Module-specific config
          (shifts, leave types, pay components) lives inside its own module.
        </p>
      </div>

      <Tabs
        tabs={visibleTabs.map((k) => ({ key: k, label: TAB_LABELS[k] }))}
        active={active}
        onChange={onChange}
      />

      {active === 'departments'    && <DepartmentsSection />}
      {active === 'designations'   && <DesignationsSection />}
      {active === 'leave_config'   && <LeaveConfigSection />}
      {active === 'penalty_config' && <PenaltyConfigSection />}
      {active === 'salary_multipliers'      && <SalaryMultipliersSection />}
      {active === 'loans_advances'          && <LoansAdvancesConfigSection />}
      {active === 'recruitment'             && <RecruitmentConfigSection />}
      {active === 'delete_punch_permission' && <DeletePunchPermissionSection />}
      {active === 'delete_access_config'    && <DeleteAccessSection />}
      {active === 'wati'                    && <MessageConfigSection />}
      {active === 'face_scan_keystores'     && <FaceScanKeystoresSection />}
    </div>
  );
}
